/**
 * Availability & rules (section `availability`, plan 6.D).
 * Group 1 Schedule & duration · Group 2 Event validity · Group 3 Booking rules · Group 4 Calendar conflicts.
 */

import React, { useMemo, useState } from "react";
import moment from "moment";
import { Link } from "react-router-dom";
import { notification } from "../../../../../utils/utility";
import { mapCalendarFirstDayToMoment, mapDatePickerFormatToMoment } from "../../../../../utils/eventConfigHelpers";
import { getAdminBusinessId } from "../../../../../utils/authHelper";

import Icon from "../../ui/Icon";
import { FieldError, Helper, SettingLine, SettingsGroup, Switch } from "../../ui/primitives";
import { useBuilder } from "../../state/builderContext";
import {
  ASSIGNMENT_MODE,
  BUFFER_OPTIONS,
  CALENDAR_APP_IDS,
  D,
  DATE_RANGE_DEFAULT_SPAN_DAYS,
  DURATION_MIN,
  DURATION_STEP,
  E,
  INCREMENT_OPTIONS,
  MAX_NOTICE_OPTIONS,
  MIN_NOTICE_OPTIONS,
  ROUTES,
  SCHEDULE_SOURCE,
  SCHEDULE_TYPE,
  STEPS,
  VALIDITY,
  VALID_OVER_DEFAULT_DAYS,
} from "../../state/constants";
import { companyTemplates, defaultCompanyTemplate, staffTemplates } from "../../state/hydrate";
import { dayRangeLabel, durationMinutes, inviteeChoosesDuration, selectedStaff, selectedTemplate } from "../../state/selectors";
import * as api from "../../state/api";
import SettingsView from "../shared/SettingsView";
import DateField from "../shared/DateField";
import useScheduleOps, { staffRowOf } from "../shared/useScheduleOps";
import WeeklyList from "./WeeklyList";
import CustomDates from "./CustomDates";

// ---- helpers ----------------------------------------------------------------------------------------------------

const intervalErrorsFor = (issues, prefix) =>
  (issues || []).reduce((acc, i) => {
    if (i.field.startsWith(prefix)) acc[i.field.slice(prefix.length)] = i.message;
    return acc;
  }, {});

const issueFor = (issues, field) => {
  const i = (issues || []).find((x) => x.field === field);
  return i ? i.message : null;
};

/** Select with a trailing `Custom…` option that swaps in a number input, so any stored value round-trips. */
const RuleSelect = ({ label, options, value, onChange, unit = "minutes", customLabel = "Custom\u2026", min = 0, step = 1, toInput = (v) => v, fromInput = (v) => v }) => {
  const n = parseInt(value, 10) || 0;
  const known = options.some((o) => o.value === n);
  const [custom, setCustom] = useState(!known);
  const showCustom = custom || !known;
  return (
    <label>
      {label}
      <select
        value={showCustom ? "custom" : String(n)}
        onChange={(e) => {
          if (e.target.value === "custom") {
            setCustom(true);
            return;
          }
          setCustom(false);
          onChange(parseInt(e.target.value, 10));
        }}
      >
        {options.map((o) => (
          <option key={o.value} value={String(o.value)}>
            {o.label}
          </option>
        ))}
        <option value="custom">{customLabel}</option>
      </select>
      {showCustom ? (
        <span className="eb-rule-custom">
          <input type="number" min={min} step={step} value={toInput(n)} onChange={(e) => onChange(e.target.value === "" ? 0 : fromInput(parseInt(e.target.value, 10) || 0))} aria-label={`${label} (custom)`} />
          <small>{unit}</small>
        </span>
      ) : null}
    </label>
  );
};

// ---- Group 1 ---------------------------------------------------------------------------------------------------------

const ScheduleGroup = () => {
  const { state, update, issues, redux, reloadTemplates } = useBuilder();
  const ops = useScheduleOps();
  const a = state.availability;
  const { scheduleTemplates, timezones } = state.lookups;
  const templates = companyTemplates(scheduleTemplates);
  const eventRow = ops.eventRow;
  const rows = eventRow ? eventRow.schedules || [] : [];
  const isTemplate = a.scheduleSource === SCHEDULE_SOURCE.TEMPLATE;
  const inviteeChooses = inviteeChoosesDuration(state);
  const minutes = durationMinutes(state);
  const [editDuration, setEditDuration] = useState(false);
  const [view, setView] = useState("list");
  const [templateBusy, setTemplateBusy] = useState(false);
  const [saveAs, setSaveAs] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [savingTemplate, setSavingTemplate] = useState(false);
  const tpl = selectedTemplate(state);
  const errors = useMemo(() => intervalErrorsFor(issues, "interval:"), [issues]);
  const tzError = issueFor(issues, "timezone_id");
  const rowsError = issueFor(issues, "schedules");

  const onTemplateChange = async (e) => {
    const v = e.target.value;
    if (!v) return;
    setTemplateBusy(true);
    await ops.applyTemplate(v);
    setTemplateBusy(false);
  };

  // Save as new schedule template — exact payload of Event-Availbility-Component.saveNewSchedule
  const saveTemplate = async () => {
    if (!a.timezone_id) {
      notification.warning("Please select timezone first");
      return;
    }
    const name = templateName.trim();
    if (!name) return;
    setSavingTemplate(true);
    try {
      await api.saveScheduleTemplate({
        schedule_name: name,
        business_id: getAdminBusinessId(),
        type: SCHEDULE_TYPE.EVENT,
        staff_id: "",
        timezone_id: a.timezone_id,
        schedule_details: rows.map((r) => ({
          weekday_id: r.dated ? null : r.dayId,
          dated: r.dated || null,
          on_status: E,
          type: r.dated ? "custom" : "weekly",
          schedule_availability: (r.schedules || []).filter((s) => s.start_time && s.end_time).map((s) => ({ start_time: s.start_time, end_time: s.end_time })),
        })),
      });
      notification.success("Schedule added successfully");
      setSaveAs(false);
      setTemplateName("");
      reloadTemplates();
    } catch (err) {
      notification.error("An error during save, please try later");
    } finally {
      setSavingTemplate(false);
    }
  };

  // per-staff hours (assignment type 2 with selected staff)
  const canStaffHours = redux.canUseStaff && (a.assignmentMode || ASSIGNMENT_MODE.MY) !== ASSIGNMENT_MODE.MY && (a.staffs || []).length > 0;
  const staffList = selectedStaff(state);
  const [staffId, setStaffId] = useState("");
  const activeStaffId = staffId || (staffList[0] ? String(staffList[0].id) : "");
  const staffRow = activeStaffId ? staffRowOf(a.schedules, activeStaffId) : null;
  const staffErrors = useMemo(() => intervalErrorsFor(issues, `staff:${activeStaffId}:interval:`), [issues, activeStaffId]);
  const staffTpls = activeStaffId ? staffTemplates(scheduleTemplates, activeStaffId) : [];
  const activeStaff = staffList.find((s) => String(s.id) === String(activeStaffId));

  return (
    <SettingsGroup
      id="schedule-setting"
      title="Schedule & duration"
      text="Control when invitees can find and book a time."
      action={
        <button type="button" className="eb-outline-small" onClick={() => setEditDuration((v) => !v)}>
          {inviteeChooses ? "Invitee chooses duration" : `Edit ${minutes} min duration`}
        </button>
      }
    >
      {editDuration && !inviteeChooses ? (
        <label className="eb-capacity-field" id="duration-inline">
          Minutes
          <input
            type="number"
            min={DURATION_MIN}
            step={DURATION_STEP}
            value={minutes || ""}
            onChange={(e) => update(STEPS.AVAILABILITY, { event_duration_in_minutes: e.target.value === "" ? "" : parseInt(e.target.value, 10) || 0, invitee_set_duration_status: D })}
          />
          <FieldError message={issueFor(issues, "event_duration_in_minutes")} />
        </label>
      ) : null}

      <SettingLine compact title="Let invitee choose the event duration" text={<>Offer more than one meeting length on the booking page. <span className="eb-coming-badge">Coming soon</span></>}>
        <Switch on={inviteeChooses} label="Invitee duration" onChange={(v) => update(STEPS.AVAILABILITY, { invitee_set_duration_status: v ? E : D })} />
      </SettingLine>

      <div className="eb-source-choice">
        <button
          type="button"
          className={isTemplate ? "selected" : ""}
          disabled={!templates.length}
          onClick={() => {
            if (isTemplate) return;
            const target = tpl || defaultCompanyTemplate(templates) || templates[0];
            if (target) ops.applyTemplate(target.value);
          }}
        >
          <Icon name="calendar" size={18} />
          <span>
            <strong>Use an existing schedule</strong>
            <small>{tpl ? `${tpl.label} \u00B7 ${dayRangeLabel(state)}` : templates.length ? "Pick a saved schedule" : "No saved schedules"}</small>
          </span>
        </button>
        <button type="button" className={!isTemplate ? "selected" : ""} onClick={() => isTemplate && ops.switchToCustomHours()}>
          <Icon name="clock" size={18} />
          <span>
            <strong>Set custom hours</strong>
            <small>Hours used only for this event</small>
          </span>
        </button>
      </div>

      <div className="eb-schedule-toolbar" id="schedule-toolbar">
        {isTemplate ? (
          <label>
            Schedule
            <select value={String(a.templateId || "")} onChange={onTemplateChange} disabled={templateBusy}>
              {!a.templateId ? <option value="">Choose a schedule</option> : null}
              {templates.map((t) => (
                <option key={t.value} value={String(t.value)}>
                  {t.label}
                </option>
              ))}
            </select>
          </label>
        ) : (
          <label>
            Schedule
            <span className="eb-toolbar-actions">
              <button type="button" className="eb-outline-small" onClick={() => setSaveAs((v) => !v)}>
                Save as template
              </button>
            </span>
          </label>
        )}
        <label className={tzError ? "eb-has-error" : ""}>
          Time zone
          {a.timezone_id ? (
            <span className="eb-tz-fixed">
              {((timezones || []).find((t) => String(t.value) === String(a.timezone_id)) || {}).label || "Your account time zone"}
            </span>
          ) : (
            <select value="" onChange={(e) => ops.setTimezone(e.target.value)} aria-label="Time zone">
              <option value="">Choose a time zone</option>
              {(timezones || []).map((t) => (
                <option key={t.value} value={String(t.value)}>
                  {t.label}
                </option>
              ))}
            </select>
          )}
          <FieldError message={tzError} />
        </label>
        <div className="eb-view-toggle" role="tablist">
          <button type="button" role="tab" aria-selected={view === "list"} className={view === "list" ? "selected" : ""} onClick={() => setView("list")}>
            List
          </button>
          <button type="button" role="tab" aria-selected={view === "calendar"} className={view === "calendar" ? "selected" : ""} onClick={() => setView("calendar")}>
            Calendar
          </button>
        </div>
      </div>

      {saveAs && !isTemplate ? (
        <div className="eb-save-template">
          <label className="eb-capacity-field">
            Template name
            <input type="text" value={templateName} onChange={(e) => setTemplateName(e.target.value)} maxLength={100} />
          </label>
          <button type="button" className="eb-outline-small" onClick={saveTemplate} disabled={savingTemplate || !templateName.trim()}>
            {savingTemplate ? "Saving\u2026" : "Save"}
          </button>
        </div>
      ) : null}

      {isTemplate ? (
        <div className="eb-template-note">
          <Helper>Hours come from the selected schedule. Edit them here to use custom hours for this event only.</Helper>
          <button type="button" className="eb-outline-small" onClick={ops.switchToCustomHours}>
            Edit as custom hours
          </button>
        </div>
      ) : null}

      {view === "list" ? (
        <WeeklyList id="weekly-list" rows={rows} readOnly={isTemplate} errors={errors} onChange={ops.setEventRows} />
      ) : (
        <CustomDates rows={rows} readOnly={isTemplate} onChange={ops.setEventRows} />
      )}
      <FieldError message={rowsError} />
      {view === "list" && rows.some((r) => r.dated) ? (
        <Helper>
          {rows.filter((r) => r.dated).length} date{rows.filter((r) => r.dated).length === 1 ? "" : "s"} with custom hours — see the Calendar view.
        </Helper>
      ) : null}

      {canStaffHours && staffList.length ? (
        <div className="eb-settings-subsection stacked" id="staff-hours">
          <div>
            <strong>Staff hours</strong>
            <p>Each host can use their own hours for this event.</p>
          </div>
          <div className="eb-staff-hours">
            <div className="eb-staff-hours-toolbar">
              <select value={activeStaffId} onChange={(e) => setStaffId(e.target.value)} aria-label="Staff member">
                {staffList.map((s) => (
                  <option key={s.id} value={String(s.id)}>
                    {s.name}
                  </option>
                ))}
              </select>
              <div className="eb-view-toggle" role="radiogroup" aria-label="Staff hours source">
                <button type="button" className={!staffRow ? "selected" : ""} onClick={() => staffRow && ops.removeStaffRow(activeStaffId)}>
                  Use event hours
                </button>
                <button type="button" className={staffRow ? "selected" : ""} onClick={() => !staffRow && ops.seedStaffRow(activeStaffId, { fallbackToEvent: true })}>
                  Custom hours{activeStaff ? ` for ${activeStaff.name.split(" ")[0]}` : ""}
                </button>
              </div>
            </div>
            {staffRow ? (
              <>
                {staffTpls.length ? (
                  <label className="eb-capacity-field">
                    Start from a schedule
                    <select value="" onChange={(e) => e.target.value && ops.applyStaffTemplate(activeStaffId, e.target.value)}>
                      <option value="">{"Copy a saved schedule\u2026"}</option>
                      {staffTpls.map((t) => (
                        <option key={t.value} value={String(t.value)}>
                          {t.label}
                        </option>
                      ))}
                    </select>
                  </label>
                ) : null}
                <WeeklyList rows={staffRow.schedules || []} errors={staffErrors} onChange={(next) => ops.setStaffRows(activeStaffId, next)} />
              </>
            ) : null}
          </div>
        </div>
      ) : null}
    </SettingsGroup>
  );
};

// ---- Group 2 ---------------------------------------------------------------------------------------------------------

const ValidityGroup = () => {
  const { state, update, issues } = useBuilder();
  const a = state.availability;
  const { datePickerFormat, calendarFirstDay } = state.lookups;
  const format = mapDatePickerFormatToMoment(datePickerFormat || "mm/dd/yyyy");
  const firstDay = mapCalendarFirstDayToMoment(calendarFirstDay || "1");
  const v = a.event_validity || VALIDITY.INDEFINITE;

  const choose = (value) => {
    const patch = { event_validity: value };
    if (value === VALIDITY.VALID_OVER && !(parseInt(a.valid_over_period, 10) > 0)) patch.valid_over_period = VALID_OVER_DEFAULT_DAYS;
    if (value === VALIDITY.DATE_RANGE && !a.event_start_date && !a.event_end_date) {
      // exactly today's page: today → today + 14 days
      patch.event_start_date = moment().format("YYYY-MM-DD");
      patch.event_end_date = moment().add(DATE_RANGE_DEFAULT_SPAN_DAYS, "d").format("YYYY-MM-DD");
    }
    update(STEPS.AVAILABILITY, patch);
  };

  const opt = (value, title, sub) => (
    <label className={v === value ? "selected" : ""} key={value}>
      <input type="radio" name="validity" checked={v === value} onChange={() => choose(value)} />
      <span>
        <strong>{title}</strong>
        <small>{sub}</small>
      </span>
    </label>
  );
  const datesError = issueFor(issues, "event_dates") || issueFor(issues, "event_end_date");

  return (
    <SettingsGroup id="validity-setting" title="Event validity" text="Choose how far into the future this event can be booked.">
      <div className="eb-validity-options">
        {opt(VALIDITY.INDEFINITE, "Indefinitely", "Always available")}
        {opt(VALIDITY.VALID_OVER, v === VALIDITY.VALID_OVER && a.valid_over_period ? `Next ${a.valid_over_period} days` : "Next … days", "A rolling booking window")}
        {opt(VALIDITY.DATE_RANGE, "Date range", "Choose start and end dates")}
      </div>
      {v === VALIDITY.VALID_OVER ? (
        <label className="eb-capacity-field">
          Days
          <input type="number" min="1" value={a.valid_over_period === "" ? "" : a.valid_over_period} onChange={(e) => update(STEPS.AVAILABILITY, { valid_over_period: e.target.value === "" ? "" : parseInt(e.target.value, 10) || "" })} />
          <FieldError message={issueFor(issues, "valid_over_period")} />
        </label>
      ) : null}
      {v === VALIDITY.DATE_RANGE ? (
        <>
          <div className="eb-date-range">
            <DateField value={a.event_start_date} format={format} firstDay={firstDay} ariaLabel="Start date" hasError={Boolean(datesError)} onChange={(iso) => update(STEPS.AVAILABILITY, { event_start_date: iso })} />
            <span>to</span>
            <DateField value={a.event_end_date} format={format} firstDay={firstDay} min={a.event_start_date} ariaLabel="End date" hasError={Boolean(datesError)} onChange={(iso) => update(STEPS.AVAILABILITY, { event_end_date: iso })} />
          </div>
          <FieldError message={datesError} />
        </>
      ) : null}
    </SettingsGroup>
  );
};

// ---- Group 3 ---------------------------------------------------------------------------------------------------------

const RulesGroup = () => {
  const { state, update, issues } = useBuilder();
  const a = state.availability;
  const d = state.details;
  const setA = (patch) => update(STEPS.AVAILABILITY, patch);
  const setD = (patch) => update(STEPS.DETAILS, patch);
  const numOrZero = (v) => (v === "" ? 0 : parseInt(v, 10) || 0);

  return (
    <SettingsGroup id="rules-setting" title="Booking rules" text="Notice, increments, buffers, and capacity limits.">
      <div className="eb-form-settings-grid rule-grid">
        <RuleSelect label="Minimum advance notice" options={MIN_NOTICE_OPTIONS} value={a.min_scheduling_notice_minutes} onChange={(v) => setA({ min_scheduling_notice_minutes: v })} unit="minutes" />
        <RuleSelect
          label="Maximum advance time"
          options={MAX_NOTICE_OPTIONS}
          value={a.max_scheduling_notice_minutes}
          onChange={(v) => setA({ max_scheduling_notice_minutes: v })}
          unit="days"
          toInput={(m) => Math.round(m / 1440)}
          fromInput={(days) => days * 1440}
        />
        <RuleSelect label="Start-time increments" options={INCREMENT_OPTIONS} value={a.slot_duration_increment_of} onChange={(v) => setA({ slot_duration_increment_of: v })} step={5} />
        <RuleSelect label="Buffer before event" options={BUFFER_OPTIONS} value={a.buffer_before_event_minutes} onChange={(v) => setA({ buffer_before_event_minutes: v })} step={5} />
        <RuleSelect label="Buffer after event" options={BUFFER_OPTIONS} value={a.buffer_after_event_minutes} onChange={(v) => setA({ buffer_after_event_minutes: v })} step={5} />
        <label>
          Maximum events per day
          <input type="number" placeholder="No limit" min="1" value={numOrZero(a.max_event_per_day) || ""} onChange={(e) => setA({ max_event_per_day: numOrZero(e.target.value) })} />
        </label>
        <label>
          Maximum events per week
          <input type="number" placeholder="No limit" min="1" value={numOrZero(a.max_event_per_week) || ""} onChange={(e) => setA({ max_event_per_week: numOrZero(e.target.value) })} />
        </label>
      </div>

      <div className="eb-compact-switch-grid">
        <div>
          <SettingLine compact id="max-bookings-setting" title="Maximum number of bookings" text="Cap total bookings for this event.">
            <Switch on={d.max_no_booking_status === E} label="Maximum bookings" onChange={(v) => setD({ max_no_booking_status: v ? E : D })} />
          </SettingLine>
          {d.max_no_booking_status === E ? (
            <label className="eb-capacity-field">
              Bookings
              <input type="number" min="1" value={d.max_no_of_booking === "" ? "" : d.max_no_of_booking} onChange={(e) => setD({ max_no_of_booking: e.target.value === "" ? "" : parseInt(e.target.value, 10) || 0 })} />
              <FieldError message={issueFor(issues, "max_no_of_booking")} />
            </label>
          ) : null}
        </div>
        <SettingLine compact title="Recurring bookings" text="Allow invitees to repeat this event.">
          <Switch on={d.recurring_booking_status === E} label="Recurring bookings" onChange={(v) => setD({ recurring_booking_status: v ? E : D })} />
        </SettingLine>
        <SettingLine compact title="Allow rescheduling" text="Invitees can choose another time.">
          <Switch on={d.allow_rescheduling_status === E} label="Allow rescheduling" onChange={(v) => setD({ allow_rescheduling_status: v ? E : D })} />
        </SettingLine>
        <SettingLine compact title="Allow cancellation" text="Invitees can cancel from their email.">
          <Switch on={d.attendee_cancellation_status === E} label="Allow cancellation" onChange={(v) => setD({ attendee_cancellation_status: v ? E : D })} />
        </SettingLine>
      </div>
    </SettingsGroup>
  );
};

// ---- Group 4 ---------------------------------------------------------------------------------------------------------

const calendarMeta = (row) => {
  try {
    const meta = typeof row.meta_data === "string" ? JSON.parse(row.meta_data || "{}") : row.meta_data || {};
    return meta || {};
  } catch (e) {
    return {};
  }
};

const CalendarGroup = () => {
  const { state } = useBuilder();
  const calendars = state.lookups.connectedCalendars || [];
  if (!calendars.length) return null;
  const name = (row) => (parseInt(row.app_id, 10) === CALENDAR_APP_IDS.GOOGLE ? "Google Calendar" : parseInt(row.app_id, 10) === CALENDAR_APP_IDS.OUTLOOK ? "Outlook Calendar" : "Calendar");
  return (
    <SettingsGroup id="calendar-setting" title="Calendar conflicts" text="Check busy calendars and choose where new bookings are added.">
      {calendars.map((row) => {
        const meta = calendarMeta(row);
        const google = parseInt(row.app_id, 10) === CALENDAR_APP_IDS.GOOGLE;
        return (
          <div className="eb-calendar-account" key={row.id}>
            {google ? <span className="eb-google-g">G</span> : <span className="eb-calendar-color" />}
            <div>
              <strong>{name(row)}</strong>
              <small>{meta.email || meta.name || "Connected"}</small>
            </div>
            <Switch on disabled label={`Check ${name(row)}`} onChange={() => {}} />
          </div>
        );
      })}
      <div className="eb-settings-subsection stacked">
        <div>
          <strong>Add new bookings to</strong>
          <p>One calendar receives the booking.</p>
        </div>
        <select disabled value="0" aria-label="Destination calendar">
          <option value="0">
            {name(calendars[0])} {calendarMeta(calendars[0]).email ? `\u00B7 ${calendarMeta(calendars[0]).email}` : ""}
          </option>
        </select>
      </div>
      <Helper>
        <Link to={ROUTES.CALENDAR_INTEGRATION}>Manage in Integrations</Link>
      </Helper>
    </SettingsGroup>
  );
};

// ---- Section ------------------------------------------------------------------------------------------------------------

const AvailabilitySection = () => (
  <SettingsView icon="calendar" title="Availability & rules" text="Schedules, booking windows, buffers, limits, and calendar conflicts.">
    <ScheduleGroup />
    <ValidityGroup />
    <RulesGroup />
    <CalendarGroup />
  </SettingsView>
);

export default AvailabilitySection;
